import React, {useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Popover, Drawer, Menu, message, Popconfirm, notification, Button } from 'antd';
import {updateUserPortfolioData, deletePortfolios, updatePortfolioDetails} from "../../../Api/portfolioApi";
import {RiMenuUnfold3Fill, RiCheckboxBlankLine} from "@remixicon/react";
import PortfolioForm from "../PortfolioForm/PortfolioForm";
import TableColumnConfig from "./components/TableColumnConfig";
import Filters from "../../../Components/Filters/Filters";
import {sortByInUse} from "../../../Components/Filters/utils";
import {filterConfigs} from "./utils";
import PortfolioPageForm from "../PortfolioPages/PortfolioPageForm";
import TableSearch from "../../../Components/TableSearch";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const SelectedPortfoliosMenu = ({ selectedPortfolios, handleRemove, setNewPageDrawerVisible }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const actions = [
    { icon: <AddIcon />, name: 'Add to New Page', onClick: () => setNewPageDrawerVisible(true) },
    { icon: <DeleteIcon />, name: 'Delete', onClick: handleRemove },
  ];

  return (
    <SpeedDial
      ariaLabel="Selected Portfolios Menu"
      icon={<SpeedDialIcon />}
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
      direction="right"
      sx={{
                   top: 16,
          left: 16,
          '& .MuiSpeedDial-fab': {
            width: 40,
            height: 40,
            '& .MuiSpeedDialIcon-icon': {
              fontSize: 20,
            },
          },
      }}

    >
      {actions.map((action) => (
        <SpeedDialAction
          key={action.name}
          icon={action.icon}
          tooltipTitle={action.name}
          onClick={() => {
            action.onClick();
            handleClose();
          }}
        />
      ))}
    </SpeedDial>
  );
};

const PortfolioCollectionTable = ({ portfolios, prefix }) => {
  const dispatch = useDispatch();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [filteredPortfolios, setFilteredPortfolios] = useState(portfolios); // New state for filtered portfolios
  const [originalPortfolios, setOriginalPortfolios] = useState(portfolios);
  const [selectedPortfolios, setSelectedPortfolios] = useState([]); // New state for selected portfolio
  const [newPageDrawerVisible, setNewPageDrawerVisible] = useState(false);

  const handleUpdatePortfolioDetails = useCallback((payload) => {
    dispatch(updatePortfolioDetails({ data: payload.data, portfolioId: payload.portfolioId }))
        .then((response) => {
            console.log(response)
            // Assuming response contains the updated portfolio details
            if (response?.meta?.requestStatus === 'fulfilled') {
                setFilteredPortfolios(prevPortfolios =>
                    prevPortfolios.map(p => p.id === payload.portfolioId ? { ...p, ...payload.data } : p)
                );
                setOriginalPortfolios(prevPortfolios =>
                    prevPortfolios.map(p => p.id === payload.portfolioId ? { ...p, ...payload.data } : p)
                );
                message.success('Portfolio updated successfully!');
            } else {
                message.error('Failed to update the portfolio.');
            }
        })
        .catch(error => {
            message.error('Error updating portfolio.');
        });
}, [dispatch]);


  const handleFilteredDataChange = (filteredData) => {
    setFilteredPortfolios(filteredData.length > 0 ? filteredData : originalPortfolios);
  };

  useEffect(() => {setOriginalPortfolios(portfolios)}, [portfolios]);

  const finalFilteredPortfolios = useMemo(() => {
    return filteredPortfolios.length ? filteredPortfolios : [];
  }, [filteredPortfolios]);

  sortByInUse(finalFilteredPortfolios);

  // Optimistic Update for toggling favorite
  const toggleFavorite = (portfolioID, isStarred) => {
      setFilteredPortfolios(prevPortfolios =>
        prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: isStarred } } : portfolio)
      );
      setOriginalPortfolios(prevPortfolios =>
        prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: isStarred } } : portfolio)
      );
      dispatch(updateUserPortfolioData({portfolioID, updateFields: { in_use: isStarred }})).catch(error => {
        setFilteredPortfolios(prevPortfolios =>
          prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: !isStarred } } : portfolio)
        );
        setOriginalPortfolios(prevPortfolios =>
          prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: !isStarred } } : portfolio)
        );
      });
    };


  const handleEdit = (portfolioId) => {
    setEditingPortfolioId(portfolioId);
    setDrawerVisible(true);
  };

  const handleRemove = () => {
    if (selectedPortfolios.length > 0) {
      dispatch(deletePortfolios({ portfolioIds: selectedPortfolios }))
        .then(() => {
            notification.success({
                message: 'Portfolios Removed',
                description: 'Portfolios successfully removed '
            })
        })
        .catch((error) => {
          message.error(`An error occurred while removing portfolios. ${error}`);
        });
    }
    else {
        message.error("No portfolios selected to remove")
    }
  };

  const checkboxTitle = selectedPortfolios.length > 0 ? <SelectedPortfoliosMenu {...{selectedPortfolios, handleRemove, setNewPageDrawerVisible}}/>:<RiCheckboxBlankLine size={16}/>
  let columns = TableColumnConfig({
      handleEdit,
      toggleFavorite,
      prefix,
  });

  const onSelectedRowKeysChange = (selectedRowKeys) => {
      setSelectedPortfolios(selectedRowKeys);
  }

  return (
      <>
          <Filters data={originalPortfolios}
                   configs={filterConfigs}
                   onFilterChange={handleFilteredDataChange} />
        <div>
            <TableSearch
               dataSource={finalFilteredPortfolios}
               columns={columns}
               className={''}
               rowKey={'id'}
               pagination={{ pageSize: 100 }}
               style={{fontSize: '11px'}}
               scroll={{ x:'100%', y: 800}}
               rowSelection={{
                   selectedRowKeys: selectedPortfolios,
                   onChange: onSelectedRowKeysChange,
                   columnTitle: checkboxTitle,
                   fixed: true
               }}
            />
            <Drawer
                title="Edit Portfolio"
                width={720}
                onClose={()=>{
                    setDrawerVisible(false);
                    setEditingPortfolioId(null);
                }}
                visible={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                {editingPortfolioId && (
                  <PortfolioForm
                    portfolioId={editingPortfolioId}
                    initialValues={finalFilteredPortfolios.find(p => p.id === editingPortfolioId)}
                    handleSubmit={handleUpdatePortfolioDetails}
                    // Pass any additional props needed for the PortfolioForm
                  />
                )}
            </Drawer>
            <Drawer
                title="Add New Portfolio Page"
                width={720}
                onClose={() => setNewPageDrawerVisible(false)}
                visible={newPageDrawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <PortfolioPageForm
                    initialValues={{ portfolio_ids: [...selectedPortfolios] }} // Pass selected portfolios as initial values
                    onSubmit={(values)=>{console.log(values)}} // You need to define this function to handle form submission
                />
            </Drawer>
      </div>
      </>
  )
};

export default PortfolioCollectionTable;
